import {
  ChangeEvent,
  createRef,
  forwardRef,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { Stack, StackProps } from '../../layout';
import { InputPin, InputPinProps, InputPinAttributes } from '../InputPin';
import './PinGroup.scss';

const gapSizes: Record<NonNullable<InputPinProps['size']>, NonNullable<StackProps['gap']>> = {
  large: 'spacing-5',
  medium: 'spacing-5',
  small: 'spacing-4',
  xs: 'spacing-3',
};
export interface PinGroupProps extends InputPinProps {
  digitsLength?: number;
  autoFocus?: boolean;
  className?: string;
  justifyContent?: React.CSSProperties['justifyContent'];
  /** Duration before the error state clears (ms). Default: 1000. */
  errorTimeout?: number;
}

export interface State {
  values: string[];
  hasError?: boolean;
}

export type inputRefType = React.RefObject<HTMLInputElement>;
export type useStateWithKeysProps = {
  defaultValue: NonNullable<InputPinAttributes['defaultValue']>;
  digitsLength: number;
};
export type getNavigationOptionsProps = {
  refs: inputRefType[];
  target: EventTarget | HTMLInputElement;
};

/**
 * Get Navigation Options
 * @param refs - Array of input refs.
 * @param target - Target input element.
 * @returns Object containing index, hasNext, hasPrevious, nextInput, previousInput.
 */
export const getNavigationOptions = ({ refs, target }: getNavigationOptionsProps) => {
  const index = refs.findIndex(ref => ref.current === target);
  const hasNext = index + 1 < refs.length;
  const hasPrevious = index > 0;
  const nextInput = hasNext ? refs[index + 1].current : undefined;
  const previousInput = hasPrevious ? refs[index - 1].current : undefined;

  return { index, hasNext, hasPrevious, nextInput, previousInput };
};

/**
 * Use State With Keys
 * @param defaultValue - Default value for the input fields.
 * @param digitsLength - Number of input fields.
 * @returns Object containing state, setState, inputRefs, changeHandler, keydownHandler.
 */
export const useStateWithKeys = ({ defaultValue, digitsLength }: useStateWithKeysProps) => {
  const inputRefs = useMemo(
    () => Array.from({ length: digitsLength }, () => createRef<HTMLInputElement>()),
    [digitsLength],
  );

  const [state, setState] = useState<State>({
    values: [...defaultValue.toString().split('')],
    hasError: false,
  });

  const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    const { index, hasNext, hasPrevious, nextInput, previousInput } = getNavigationOptions({
      refs: inputRefs,
      target,
    });

    const isDigit = /^\d+$/.test(target.value);
    const isEmpty = target.value === '';

    // set group state with new value on the corresponding index
    setState(prevState => {
      const values = [...(prevState.values || [])];
      values[index] = target.value.charAt(target.value.length - 1);
      return { ...prevState, values };
    });

    // moves focus to the next input field if digit is entered
    if (isDigit && hasNext) nextInput?.focus();

    // moves focus to the previous input field if input is empty and backspace is pressed
    if (isEmpty && hasPrevious) previousInput?.focus();
  };

  const keydownHandler = (e: KeyboardEvent<HTMLInputElement>) => {
    const { hasNext, hasPrevious, nextInput, previousInput, index } = getNavigationOptions({
      refs: inputRefs,
      target: e.target,
    });

    if (
      (e.key === 'ArrowRight' && !hasNext) ||
      (e.key === 'ArrowLeft' && !hasPrevious) ||
      (e.key === 'Tab' && ((hasNext && !e.shiftKey) || (hasPrevious && e.shiftKey)))
    ) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (e.key === 'Tab') {
      if (e.shiftKey && hasPrevious) previousInput?.focus();
      else if (!e.shiftKey && hasNext) nextInput?.focus();
    }

    if (e.key === 'ArrowLeft') previousInput?.focus();
    else if (e.key === 'ArrowRight') nextInput?.focus();

    if (e.key === 'Backspace' && inputRefs[index]?.current?.value === '' && hasPrevious) {
      previousInput?.focus();
    }
  };

  return { state, setState, inputRefs, changeHandler, keydownHandler };
};

/**
 * Input Pin Group Component
 *
 * @param digitsLength - Number of input digits to display.
 * @param size - Size of the input fields.
 * @param name - Name for the input group.
 * @param type - Type for input fields.
 * @param onBlur - Blur callback for the input group.
 * @param onFocus - Focus callback for the input group.
 * @param onChange - Change callback for the input group.
 * @param disabled - If true, input group is disabled.
 * @param id - Set the id for the input group.
 * @param defaultValue - Set the initial value for the input group.
 * @param autoFocus - If true, first input field is focused.
 * @param required - If true, the input group is required.
 * @param error - If true, input gain error status
 * @param errorTimeout - Duration in milliseconds before the error state clears and inputs reset. Defaults to 1000.
 * @param className - Extend the styles applied to the component.
 * @param justifyContent - Align component to center, left and right.
 * @param assistiveTextId - ID of the AssistiveText element; forwarded to every InputPin's aria-describedby.
 *
 */
export const PinGroup = forwardRef<HTMLInputElement, PinGroupProps>(
  (
    {
      digitsLength = 4,
      size = 'medium',
      name,
      type = 'text',
      onBlur,
      onFocus,
      onChange,
      disabled,
      id,
      defaultValue = '',
      autoFocus,
      required,
      error,
      autoComplete,
      className,
      justifyContent = 'center',
      assistiveTextId,
      errorTimeout = 1000,
    },
    ref,
  ) => {
    const { state, setState, inputRefs, changeHandler, keydownHandler } = useStateWithKeys({
      defaultValue,
      digitsLength,
    });
    const hiddenInputRef = useRef<HTMLInputElement | null>(null);

    // fixes Property 'current' does not exist on type '((instance: HTMLDivElement...' ts issue
    const initRefs = (element: HTMLInputElement) => {
      hiddenInputRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    };

    const inputElements = Array.from({ length: digitsLength }, (_, index) => {
      return (
        <InputPin
          inputRef={inputRefs[index]}
          key={index}
          size={size}
          type={type}
          error={state.hasError}
          onChange={changeHandler}
          onKeyDown={keydownHandler}
          onFocus={onFocus}
          onBlur={onBlur}
          autoFocus={index === 0 && autoFocus}
          defaultValue={state.values[index]}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          data-testid={`pin-input-${index}`}
          assistiveTextId={assistiveTextId}
        />
      );
    });

    useEffect(() => {
      if (error) {
        const timeoutCallback = () => {
          // clear all input values and reset error
          setState({ values: Array(digitsLength).fill(''), hasError: false });
          // focus first input
          inputRefs[0].current?.focus();
        };

        setState({ ...state, hasError: true });
        const timeoutInstance = setTimeout(timeoutCallback, errorTimeout);

        return () => clearTimeout(timeoutInstance);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- omitting state and inputRefs
    }, [error]);

    useEffect(() => {
      // Change event does not fire on hidden inputs, so we need to trigger it manually
      if (onChange && hiddenInputRef.current) {
        const event = {
          bubbles: true,
          cancelable: false,
          currentTarget: hiddenInputRef.current,
          target: hiddenInputRef.current,
        } as ChangeEvent<HTMLInputElement>;

        onChange(event);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- omitting onChange
    }, [state.values]);

    return (
      <Stack
        className={classNames('vds-pin-group', className)}
        flexDirection="row"
        alignItems="center"
        justifyContent={justifyContent}
        gap={gapSizes[size]}>
        {inputElements}
        <input
          className="vds-input-hidden"
          id={id}
          ref={initRefs}
          name={name}
          value={state.values.join('')}
          type="text"
          readOnly
        />
      </Stack>
    );
  },
);
